import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BaseButton } from '@repo/common';
import { useDashboardBreakpoint } from '@repo/common';

import { ReactComponent as DashboardFilledIcon } from '@repo/common/assets/icons/24px/icon_24px_dashboard_filled.svg';
import { ReactComponent as DashboardOutlineIcon } from '@repo/common/assets/icons/24px/icon_24px_dashboard_outline.svg';
import { ReactComponent as GraphAnalyticsFilledIcon } from '@repo/common/assets/icons/24px/icon_24px_graph-analytics_filled.svg';
import { ReactComponent as GraphAnalyticsOutlineIcon } from '@repo/common/assets/icons/24px/icon_24px_graph-analytics_outline.svg';
import { ReactComponent as ProgressBarFlagFilledIcon } from '@repo/common/assets/icons/24px/icon_24px_progress-bar-flag_filled.svg';
import { ReactComponent as ProgressBarFlagOutlineIcon } from '@repo/common/assets/icons/24px/icon_24px_progress-bar-flag_outline.svg';
import { ReactComponent as LoginIcon } from '@repo/common/assets/icons/24px/icon_24px_login.svg';

const Sidebar = ({ activeIndex, setActiveIndex }) => {
  const homeImageUrl = `${process.env.REACT_APP_ASSETS_URL}/Kpathport/Dashboard/img-dashboard-home.svg`;
  const navigate = useNavigate();
  const bp = useDashboardBreakpoint();
  const isMobile = ['mobileMini', 'mobile', 'tabletH'].includes(bp);
  const isMobileMini = bp === 'mobileMini';
  const mobileHomeSize = isMobileMini ? 56 : 64;
  const mobileMenuSize = isMobileMini ? 42 : 48;
  const mobileMenuPadding = isMobileMini ? 'p-[6px]' : 'p-2';
  const mobileMenuGap = isMobileMini ? 'gap-2' : 'gap-3';
  const mobileHomeHeightClass = isMobileMini ? 'h-[56px]' : 'h-[64px]';
  const mobileHomePaddingClass = isMobileMini ? 'p-3' : 'p-[13.05px]';
  const mobileMenuButtonClass = isMobileMini
    ? 'h-[42px] p-[9px]'
    : 'h-[48px] p-3';

  const HomeIcon = (
    <img src={homeImageUrl} alt='home' className='w-full h-full' />
  );

  const menuItems = [
    { outline: DashboardOutlineIcon, filled: DashboardFilledIcon },
    { outline: GraphAnalyticsOutlineIcon, filled: GraphAnalyticsFilledIcon },
    { outline: ProgressBarFlagOutlineIcon, filled: ProgressBarFlagFilledIcon },
  ];

  const homeButton = (
    <BaseButton
      icon={HomeIcon}
      type='text'
      size='l'
      bgclass={`${
        isMobile
          ? `${mobileHomePaddingClass} ${mobileHomeHeightClass}`
          : 'px-[12.5px] py-4 h-[76px]'
      }`}
      width={`${isMobile ? `${mobileHomeSize}px` : '76px'}`}
      onClick={() => setActiveIndex(0)}
    />
  );

  const menuGroup = (
    <div
      className={`flex ${
        isMobile
          ? `flex-row ${mobileMenuPadding} ${mobileMenuGap}`
          : 'flex-col p-2 pb-4 gap-4'
      } items-center rounded-[50px] shrink-0`}
      style={{
        border: '1.6px solid transparent',
        backgroundImage: `linear-gradient(#ffffff, #ffffff), linear-gradient(130deg, rgba(198, 249, 233, 0.20) 13.25%, rgba(77, 213, 163, 0.20) 47.59%, rgba(198, 249, 233, 0.20) 81.94%), linear-gradient(rgba(85, 82, 78, 0.20), rgba(85, 82, 78, 0.10))`,
        backgroundOrigin: 'padding-box, border-box, border-box',
        backgroundClip: 'padding-box, border-box, border-box',
      }}
    >
      {menuItems.map((item, index) => {
        const Icon = activeIndex === index ? item.filled : item.outline;
        return (
          <BaseButton
            key={index}
            icon={<Icon />}
            type='text'
            size='l'
            width={`${isMobile ? `${mobileMenuSize}px` : '60px'}`}
            onClick={() => setActiveIndex(index)}
            bgclass={`${
              isMobile ? mobileMenuButtonClass : 'h-[60px] p-4'
            } rounded-[50px] ${
              activeIndex === index
                ? 'bg-green-deep05 border-[1.6px] border-surface-gray10 text-green-light02'
                : 'bg-transparent border-[1.6px] border-transparent text-green-deep04'
            }`}
          />
        );
      })}
    </div>
  );

  const logoutButton = (
    <div
      style={{
        border: '1.6px solid transparent',
        borderRadius: '50px',
        backgroundImage: `linear-gradient(#ffffff, #ffffff), linear-gradient(130deg, rgba(198, 249, 233, 0.20) 13.25%, rgba(77, 213, 163, 0.20) 47.59%, rgba(198, 249, 233, 0.20) 81.94%), linear-gradient(rgba(85, 82, 78, 0.10), rgba(85, 82, 78, 0.10))`,
        backgroundOrigin: 'padding-box, border-box, border-box',
        backgroundClip: 'padding-box, border-box, border-box',
      }}
    >
      <BaseButton
        icon={<LoginIcon />}
        type='text'
        size='l'
        onClick={() => navigate('/')}
        bgclass={`${isMobileMini ? 'p-3' : 'p-4'} ${
          isMobile ? mobileHomeHeightClass : 'h-[76px] '
        } bg-grayscale-gray00 rounded-[50px] text-green-deep04`}
        width={`${isMobile ? `${mobileHomeSize}px` : '76px'}`}
      />
    </div>
  );

  if (isMobile) {
    return (
      <div className='flex flex-row items-start justify-between w-full gap-2 pb-9'>
        {homeButton}
        {menuGroup}
        {logoutButton}
      </div>
    );
  }

  return (
    <div
      className='flex flex-col items-center self-stretch justify-between h-full max-h-[calc(100vh-80px)]'
    >
      <div className='flex flex-col items-start gap-2'>
        {homeButton}
        {menuGroup}
      </div>
      {logoutButton}
    </div>
  );
};

export default Sidebar;
